import { toast } from "react-toastify";
import {
  BaseApplicationService,
  SmartApplyApiError,
  SurveyEntity,
  reportAsyncError,
  surveyApiClient,
} from "@graffer-inc/smart-apply";
import {
  DefaultNumItemsPerPages,
  toTotalPage,
} from "../../domain/pagination/paging";
import { rebuildQuery } from "../../../utils/url-utils";
import { SurveyType } from "@graffer-inc/cloud-gov-core";
import { reportUnexpectedError } from "../../../error-reporter";

export type OrganizationSurveysServiceProps = {
  isLoading: boolean;
  surveys: Array<SurveyEntity>;
  paging: {
    current: number;
    total: number;
  };
  queryName: string;
  setPage: typeof OrganizationSurveysService.prototype.setPage;
  movePage: typeof OrganizationSurveysService.prototype.movePage;
  setName: typeof OrganizationSurveysService.prototype.setName;
  fetchSurveysByTenantId: typeof OrganizationSurveysService.prototype.fetchSurveysByTenantId;
  searchSurveys: typeof OrganizationSurveysService.prototype.searchSurveys;
  importSurveyFromFile: typeof OrganizationSurveysService.prototype.importSurveyFromFile;
};

export class OrganizationSurveysService extends BaseApplicationService<OrganizationSurveysServiceProps> {
  private readonly surveyCountPerPage: number = DefaultNumItemsPerPages;

  state = {
    isLoading: false,
    surveys: [],
    paging: {
      current: 1,
      total: 0,
    },
    queryName: "",
    setPage: this.setPage.bind(this),
    movePage: this.movePage.bind(this),
    setName: this.setName.bind(this),
    fetchSurveysByTenantId: this.fetchSurveysByTenantId.bind(this),
    searchSurveys: this.searchSurveys.bind(this),
    importSurveyFromFile: this.importSurveyFromFile.bind(this),
  };

  private setIsLoading(isLoading: boolean) {
    this.setState({
      isLoading: isLoading,
    });
  }

  setPage(p: number) {
    this.setState({
      paging: {
        ...this.state.paging,
        current: p,
      },
    });
  }

  movePage(p: number) {
    this.history.push(
      rebuildQuery({
        page: p.toString(),
      }),
    );
  }

  setName(name: string) {
    this.setState({
      queryName: name,
    });
  }

  searchSurveys() {
    this.history.replace(
      rebuildQuery({
        page: "1",
        name: this.state.queryName,
      }),
    );
  }

  async fetchSurveysByTenantId(tenantId: string) {
    this.setIsLoading(true);
    try {
      const { data: surveys, headers } =
        await surveyApiClient.fetchSurveysByTenantId(
          tenantId,
          this.state.paging.current,
          this.surveyCountPerPage,
          {
            query: this.state.queryName,
          },
        );

      this.setState({
        paging: {
          ...this.state.paging,
          total: toTotalPage(
            Number(headers["pagination-count"]),
            this.surveyCountPerPage,
          ),
        },
        surveys: surveys || [],
      });
    } catch (e) {
      reportUnexpectedError(e);
      toast.error("アンケートの取得に失敗しました。");
    } finally {
      this.setIsLoading(false);
    }
  }

  async importSurveyFromFile(
    jsonData: File,
    tenantId: string,
    groupId: string,
  ): Promise<SurveyType | void> {
    try {
      const { data } = await surveyApiClient.importSurveyFromFile(
        jsonData,
        tenantId,
        groupId,
      );
      toast.success("アンケートをインポートしました。");
      return data;
    } catch (e) {
      if (SmartApplyApiError.isErrorCode(e, "custom_url_already_exists")) {
        toast.error("URLが重複するため、登録できませんでした。");
        return;
      }
      reportAsyncError(e);
      toast.error("アンケートインポートに失敗しました。");
    }
  }
}
